import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { HeaderLottieContainer } from "./styled";
import { lottieProps } from "./constants";

import styles from "../Header.scss";
import { HeaderLottieProps } from "@swiggy-private/cart-types";
import { getCloudinaryUrl, isEmpty } from "@swiggy-private/js-utils";
import {
    CartHeaderLottieContext,
    INITIAL_CART_HEADER_LOTTIE_CONTEXT_VALUE,
} from "@cart-client/contexts/CartHeaderLottieContext";
import { getFrameData, LottieFrameData, LottieType } from "./helper";
import { LottieAnimation } from "@swiggy-private/core-ui";
import { CART_HEADER_DATA_TEST_IDS } from "@swiggy-private/cart-test-ids";

const HeaderLottie = ({ lottieArray, cartType, setHeaderLottieShown }: HeaderLottieProps) => {
    const { cartHeaderLottieState, setCartHeaderLottieState } = useContext(CartHeaderLottieContext);
    const { startAnimation, showAnimation } = cartHeaderLottieState;
    const [lottieUrl, setLottieUrl] = useState<string>("");
    const [hideLottie, setHideLottie] = useState(false);
    const [lottieFrameData, setLottieFrameData] = useState<LottieFrameData | undefined>();
    const lottieId = useMemo(
        () => lottieArray.filter((lottie) => lottie.type === LottieType.MAIN)?.[0]?.lottie_id,
        [lottieArray],
    );
    const showLottieAnimation = useMemo(() => {
        return startAnimation && showAnimation;
    }, [startAnimation, showAnimation]);
    const handleError = useCallback(() => {
        setCartHeaderLottieState({
            ...INITIAL_CART_HEADER_LOTTIE_CONTEXT_VALUE.cartHeaderLottieState,
            hideTheme: true,
        });
        setHideLottie(true);
        setHeaderLottieShown(true);

        // TO DO: ADD NR LOGS AFTER MIGRATION
    }, [setCartHeaderLottieState, setHeaderLottieShown]);

    const handleInitiateTheme = useCallback(() => {
        setCartHeaderLottieState((prev) => {
            if (!prev.hideTheme) {
                return { ...prev, themeChange: true };
            }
            return prev;
        });
    }, [setCartHeaderLottieState]);

    const onLottieCompletion = useCallback(() => {
        setHideLottie(true);
        setHeaderLottieShown(true);
    }, [setHideLottie, setHeaderLottieShown]);

    useEffect(() => {
        setLottieUrl(getCloudinaryUrl(lottieId) || "");
        setLottieFrameData(getFrameData(cartType, LottieType.MAIN));
    }, [cartType, lottieId]);

    if (hideLottie || !cartHeaderLottieState.hasLottie || isEmpty(lottieUrl)) {
        return null;
    }

    return (
        <HeaderLottieContainer data-testid={CART_HEADER_DATA_TEST_IDS.HEADER_MAIN_LOTTIE}>
            <LottieAnimation
                {...lottieProps}
                fileName={lottieUrl}
                visible={showLottieAnimation}
                playLottie={showLottieAnimation}
                autoplay={showLottieAnimation}
                className={styles.lottie}
                isOnFileChangeEnabled={true}
                onPlayComplete={onLottieCompletion}
                onFrameEventCompletion={handleInitiateTheme}
                onError={handleError}
                frameEventBreak={lottieFrameData?.transitionFrame}
                enableFrameEvent={true}
                preserveAspect="xMinYMid slice"
                frame={
                    lottieFrameData ? { initial: lottieFrameData?.initial, final: lottieFrameData?.final } : undefined
                }
            />
        </HeaderLottieContainer>
    );
};

export default memo(HeaderLottie);
